import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, FolderOpen } from "lucide-react";
import { z } from "zod";
import {
  fromYmd,
  businessDate,
  hours as formatHours,
  isValidYmd,
  isWeekend,
  isoWeek,
  mondaysOfMonth,
  nswPublicHoliday,
  weekDays,
  startOfWeek,
  weekOverlapsProject,
  weekRange,
  ymd,
  type MyTimesheetRow,
  type MyTimesheetsQuery,
} from "@contractorhub/shared";
import { myAssignmentsQuery, myTimesheetsQuery } from "../../../lib/queries";
import { CELL_STYLE, cellState, engagementsOf } from "../../../lib/week-state";
import {
  Button,
  Card,
  EmptyState,
  PageHeader,
  StatusBadge,
  cn,
} from "../../../components/ui";

const searchSchema = z.object({
  month: z.string().refine(isValidYmd).optional(), // any day inside the month
});

export const Route = createFileRoute("/_authed/_contractor/my-calendar")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ month: search.month }),
  loader: async ({ context, deps }) => {
    const anchor = deps.month ? fromYmd(deps.month) : businessDate();
    await Promise.all([
      context.queryClient.ensureQueryData(myAssignmentsQuery),
      context.queryClient.ensureQueryData(myTimesheetsQuery(monthQuery(anchor))),
    ]);
  },
  component: MyCalendarPage,
});

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DAY_HEADERS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * `days: "1"` is what makes a day grid possible.
 *
 * Without it `MyTimesheetRow` carries a week total and nothing else, so every
 * cell in a seven-column month would have to show the same number. The flag is
 * opt-in because this query is unpaginated (DATA-7) and the submissions page runs
 * it with no date range at all — see `myTimesheetsQuerySchema`. Here the range is
 * always one month, which is what makes asking for entries affordable.
 */
function monthQuery(anchor: Date): MyTimesheetsQuery {
  const mondays = mondaysOfMonth(anchor);
  const first = mondays[0] ?? startOfWeek(anchor);
  const last = mondays[mondays.length - 1] ?? first;
  return { from: ymd(first), to: ymd(last), days: "1" };
}

/** A week's hours per day, summed across every project the contractor is on. */
function dayTotalsOf(rows: MyTimesheetRow[]): number[] {
  return rows.reduce(
    (totals, row) =>
      row.dailyHours
        ? totals.map((sum, day) => sum + (row.dailyHours![day] ?? 0))
        : totals,
    Array.from({ length: 7 }, () => 0)
  );
}

function MyCalendarPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  // Business-local: the calendar must agree with the week the timesheet serves.
  const today = businessDate();

  const { data: assignmentsData } = useSuspenseQuery(myAssignmentsQuery);
  const active = assignmentsData.assignments.filter((a) => a.active);
  const engagements = engagementsOf(assignmentsData.assignments);

  const anchor = search.month ? fromYmd(search.month) : today;
  const mondays = mondaysOfMonth(anchor);

  // Every project, in one view — the calendar is no longer scoped to one.
  const { data } = useSuspenseQuery(myTimesheetsQuery(monthQuery(anchor)));

  // A week can hold several rows (one per project); group instead of last-write.
  const byWeek = new Map<string, MyTimesheetRow[]>();
  for (const t of data.timesheets) {
    const list = byWeek.get(t.weekStart) ?? [];
    list.push(t);
    byWeek.set(t.weekStart, list);
  }

  const shiftMonth = (delta: number) => {
    const next = new Date(
      Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth() + delta, 1)
    );
    void navigate({ to: "/my-calendar", search: { month: ymd(next) } });
  };

  /**
   * Paging forward stops at the current month (TIME-4).
   *
   * Every day cell in a future week is already unclickable, so a month ahead of
   * this one is a grid of dashed placeholders and nothing else — the chevron
   * offered a journey with no destination. The month *containing* today stays
   * reachable in full: its later weeks are only partly future, and its current
   * week is the one being filled.
   */
  const atCurrentMonth =
    anchor.getUTCFullYear() === today.getUTCFullYear() &&
    anchor.getUTCMonth() === today.getUTCMonth();

  if (active.length === 0) {
    return (
      <Card className="mx-auto mt-16 max-w-lg">
        <EmptyState
          icon={FolderOpen}
          title="No projects assigned"
          description="Contact your Simplok admin to get set up."
        />
      </Card>
    );
  }

  return (
    <div>
      <PageHeader
        title="Calendar"
        description="Every day of the month, and where each week stands. Select a day to open its week."
        actions={
          <div className="flex items-center gap-1">
            <Button
              variant="secondary"
              iconOnly
              aria-label="Previous month"
              onClick={() => shiftMonth(-1)}
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
            <div className="w-40 px-3 py-2 text-center text-sm font-medium">
              {MONTH_NAMES[anchor.getUTCMonth()]} {anchor.getUTCFullYear()}
            </div>
            <Button
              variant="secondary"
              iconOnly
              aria-label="Next month"
              disabled={atCurrentMonth}
              title={atCurrentMonth ? "Next month hasn't started yet" : undefined}
              onClick={() => shiftMonth(1)}
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        }
      />

      {/* The grid scrolls inside its own box rather than the page. Eight columns
          cannot compress to a phone without the day cells becoming unreadable, and
          a month you can read by scrolling sideways beats one you cannot read. */}
      <Card className="overflow-x-auto">
        <div className="min-w-[56rem]">
          <div className="grid grid-cols-[repeat(7,minmax(0,1fr))_11rem]">
            {DAY_HEADERS.map((label) => (
              <div
                key={label}
                className="border-b border-line px-3 py-2 text-micro font-medium uppercase tracking-wider text-subtle"
              >
                {label}
              </div>
            ))}
            <div className="border-b border-l border-line px-3 py-2 text-micro font-medium uppercase tracking-wider text-subtle">
              Week
            </div>

            {mondays.map((monday) => {
              const key = ymd(monday);
              const rows = byWeek.get(key) ?? [];
              const engaged = engagements.some((b) => weekOverlapsProject(monday, b));
              const state = cellState(rows, engaged, monday, today);
              const disabled = state === "Upcoming" || state === "Not engaged";
              const isCurrentWeek = key === ymd(startOfWeek(today));
              const dayTotals = dayTotalsOf(rows);
              const weekTotal = dayTotals.reduce((sum, h) => sum + h, 0);

              return (
                <div key={key} className="contents">
                  {weekDays(monday).map((date, day) => {
                    const holiday = nswPublicHoliday(date);
                    const outside = date.getUTCMonth() !== anchor.getUTCMonth();
                    const isToday = date.getTime() === today.getTime();
                    const dayHours = dayTotals[day]!;

                    return (
                      <button
                        key={ymd(date)}
                        type="button"
                        disabled={disabled}
                        onClick={() =>
                          void navigate({
                            to: "/my-timesheet",
                            search: { week: key },
                          })
                        }
                        aria-label={`${ymd(date)}${holiday ? ` — ${holiday.name}` : ""}${
                          dayHours > 0 ? ` — ${formatHours(dayHours)} hours` : ""
                        }`}
                        className={cn(
                          "min-h-[5.5rem] border-b border-line px-3 py-2 text-left transition",
                          // Weekends and out-of-month days recede rather than
                          // disappear: they are still part of the week you would
                          // open by clicking them.
                          outside
                            ? "bg-surface-sunken/60"
                            : isWeekend(date)
                              ? "bg-surface-sunken/35"
                              : "bg-surface",
                          isToday && "ring-2 ring-inset ring-accent",
                          disabled ? "cursor-not-allowed" : "hover:bg-surface-hover"
                        )}
                      >
                        <div
                          className={cn(
                            "text-xs font-medium",
                            outside ? "text-subtle" : "text-muted"
                          )}
                        >
                          {date.getUTCDate()}
                        </div>
                        {dayHours > 0 && (
                          <div className="mt-1 text-md font-semibold tabular-nums text-text">
                            {formatHours(dayHours)}
                            <span className="ml-0.5 text-xs font-normal text-subtle">
                              h
                            </span>
                          </div>
                        )}
                        {holiday && (
                          <div className="mt-1 text-micro leading-tight text-danger-on-soft">
                            {holiday.name}
                          </div>
                        )}
                      </button>
                    );
                  })}

                  <div
                    className={cn(
                      "border-b border-l border-line px-3 py-2",
                      CELL_STYLE[state],
                      isCurrentWeek && "ring-2 ring-inset ring-accent"
                    )}
                  >
                    <div className="text-micro text-subtle">
                      Week {isoWeek(monday)}
                      {isCurrentWeek && " · now"}
                    </div>
                    <div className="mt-0.5 text-sm font-semibold tabular-nums text-text">
                      {weekTotal > 0 ? `${formatHours(weekTotal)} h` : "—"}
                    </div>
                    <div className="mt-1.5">
                      <StatusBadge status={state} />
                    </div>
                    <div className="mt-1 text-micro text-muted">
                      {weekRange(monday)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>
    </div>
  );
}
